import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import Field from '../Field/Field';
import { createPortal } from 'react-dom';
import Card from '../Card/card';
import { SearchIcon2 } from '../../svgs/searchIcon2';
import serviceSearch, { Result } from '../../../services/service-search';
import { debounce } from 'lodash';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import styles from './GlobalSearch.module.scss';
import { getRandomGenerator } from '../../../util';
import { useTranslation } from 'react-i18next';

type Props = {
  show: boolean,
  setShow: (value: boolean) => void,
} & RouteComponentProps

const GlobalSearch : FC<Props> = ({ show, setShow, history }) => {
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [value, setValue] = useState('');
  const [selected, setSelected] = useState(0);
  const [results, setResults] = useState<Result[]>([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [t] = useTranslation('site', { keyPrefix: 'search' });

  const keyHandler = useCallback((e : KeyboardEvent) => {
    const os = (navigator.platform.match(/mac|win|linux/i) || ['other'])[0].toLowerCase();
    const modifierKey = os == 'mac' ? 'metaKey' : 'ctrlKey';

    if (show) {
      if (e.key == 'Escape') {
        setShow(false);
      } else if (e.key == 'ArrowUp') {
        setSelected(Math.max(0, selected - 1));
        e.stopPropagation();
      } else if (e.key == 'ArrowDown') {
        setSelected(Math.min(results.length - 1, selected + 1));
        e.stopPropagation();
      } else if (e.key == 'Enter') {
        go(results[selected].path);
        e.stopPropagation();
      }
    } else {
      if (e[modifierKey] && e.key == 'k') {
        if (e.metaKey && modifierKey != 'metaKey') return;
        if (e.ctrlKey && modifierKey != 'ctrlKey') return;
        if (e.shiftKey) return;
        if (e.altKey) return;
        setShow(true);
        e.preventDefault();
        e.stopPropagation();
      }
    }
  }, [show, selected, results]);

  useEffect(() => {
    document.addEventListener('keydown', keyHandler);
    return () => document.removeEventListener('keydown', keyHandler);
  }, [keyHandler]);

  useEffect(() => {
    if (show && searchInputRef.current) searchInputRef.current.focus();
  }, [show]);

  useEffect(() => {
    if (!show) {
      inputChange('');
      setSelected(0);
      setResults([]);
    }
  }, [show]);

  const debounced = useMemo(() => debounce(async (value) => {
    setLoading(true);
    setQuery(value);
    const results = await serviceSearch.search(value);
    setLoading(false);
    setResults(results.results);
    setSelected(0);
  }, 500), []);

  const inputChange = useCallback((value: string) => {
    setValue(value);
    debounced(value);
  }, []);

  const go = (path: string) => {
    if (path.split('/')[1] != history.location.pathname.split('/')[1]) {
      window.location.href = path; // real navigation when changing org
    } else {
      history.push(path);
    }
    setShow(false);
  }

  return createPortal(
    <div className={`${styles.globalSearch} ${show ? styles.show : ''}`} onClick={() => setShow(false)}>
      <div className={styles.container} onClick={e => e.stopPropagation()}>
        <Card>
          <Field
            value={value}
            onChange={inputChange}
            ref={searchInputRef}
            icon={<SearchIcon2 size={20} color="var(--gray-500)" />}
            onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
              if (['ArrowUp', 'ArrowDown'].includes(e.key)) e.preventDefault(); // used for selecting result, shouldn't move text input cursor
            }}
            placeholder={t('searchField.placeholder', 'Sök bland projekt, leverantörer och sidor')}
          />
          <div className={styles.results}>
            {loading ? (
              [1,2,3,4,5].map((result) =>
                <div
                  key={'placeholder' + result}
                  className={styles.loadingPlaceholder}
                  style={{ width: `calc(30% + ${Math.round(getRandomGenerator(query + result)() * 50)}%)` }}>
                </div>
              )
            ) : (
              results.length ? (
                results.map((result, i) =>
                  <div
                    className={`${styles.result} ${selected === i ? styles.selected : ''}`}
                    onMouseEnter={() => {setSelected(i)}}
                    onClick={() => go(result.path)}
                    key={result.path}
                  >
                    {result.i18nKey? t(result.i18nKey, result.title) : result.title}
                    <span className={styles.resultTag}>
                      {result?.tags?.type && (` – ${t('type.'+result.tags.type, result.tags.type)}`)}
                      {result?.tags?.org && (` – ${result.tags.org}`)}
                    </span>
                  </div>
                )
              ) : (
                <div className={styles.noResults} key={'no result'}>
                  {t('noResults', 'Inget projekt, leverantör eller sida matchade din sökning.')}
                </div>
              )
            )}
          </div>
        </Card>
      </div>
    </div>
  , document.body);
}

export default withRouter(GlobalSearch);
